import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/** One configurable cell: a label carrier this dispatch center's provider may
 *  serve, the carrier's dropoff location options, and the current default. */
export interface DispatchCenterDropoffCell {
  labelCarrier: {
    id: string;
    code: string;
    name: string;
    logoUrl?: string | null;
  };
  dropoffLocations: {
    id: string;
    name: string;
    operatingHours?: Record<string, unknown> | null;
    address?: { line1: string; city: string; province: string } | null;
  }[];
  defaultDropoffLocationId: string | null;
}

export function useGetDispatchCenterCarrierDropoffs(
  dispatchCenterId: string | null
) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["dispatch-center-dropoffs", dispatchCenterId],
    queryFn: async () => {
      const { data } = await axios.get<{
        success: boolean;
        data: DispatchCenterDropoffCell[];
      }>(
        `${API_URL}/api/v2/admin/dispatch-centers/${dispatchCenterId}/carrier-dropoffs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token && !!dispatchCenterId,
    staleTime: 30_000,
  });
}

export interface UpdateDispatchCenterDropoffInput {
  dispatchCenterId: string;
  labelCarrierId: string;
  /** null clears the default → CARRIER_DROPOFF tasks fall to the exception inbox. */
  dropoffLocationId: string | null;
}

export function useUpdateDispatchCenterCarrierDropoff(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dispatchCenterId,
      labelCarrierId,
      dropoffLocationId,
    }: UpdateDispatchCenterDropoffInput) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: {
          dispatchCenterId: string;
          labelCarrierId: string;
          defaultDropoffLocationId: string | null;
        };
      }>(
        `${API_URL}/api/v2/admin/dispatch-centers/${dispatchCenterId}/carrier-dropoff`,
        { labelCarrierId, dropoffLocationId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["dispatch-center-dropoffs", data.dispatchCenterId],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}
